import React, { useState } from "react";
import { Calendar as CalendarIcon, Users, Clock, Phone, Mail, User, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Calendar } from "@/components/ui/calendar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";

const TIME_SLOTS = [
  "11:30", "12:00", "12:30", "13:00", "13:30",
  "17:30", "18:00", "18:30", "19:00", "19:30", "20:00", "20:30",
];

export default function ReservationsPage() {
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", party_size: 2, special_requests: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const { t } = useTranslation();

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Please pick a date");
    if (!time) return toast.error("Please pick a time");
    if (!form.name || !form.phone) return toast.error("Name and phone are required");
    setSubmitting(true);
    try {
      // Format date in LOCAL timezone (avoids UTC off-by-one-day bug)
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      const iso = `${yyyy}-${mm}-${dd}`;
      const res = await api.post("/reservations", { ...form, party_size: parseInt(form.party_size, 10), date: iso, time });
      setConfirmed(res.data);
      toast.success(t("reserve.confirmed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create reservation");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="pt-32 pb-12 min-h-screen container-tulsi" data-testid="reservation-confirmed">
        <div className="max-w-xl mx-auto glass-card p-10 text-center">
          <CheckCircle2 size={56} className="text-[#D11D21] mx-auto mb-4" />
          <h1 className="font-serif-display text-4xl text-gold-gradient mb-2">{t("reserve.confirmed")}</h1>
          <p className="text-[#6B4A4E] mb-6">{t("reserve.willConfirm")}</p>
          <div className="text-left p-5 rounded-md bg-[#FFFAEF]/70 border border-[#E8D9BE] space-y-2">
            <p><span className="text-[#6B4A4E]">{t("reserve.name")}:</span> <span className="text-[#2A0708]">{confirmed.name}</span></p>
            <p><span className="text-[#6B4A4E]">{t("reserve.date")}:</span> <span className="text-[#2A0708]">{confirmed.date} · {confirmed.time}</span></p>
            <p><span className="text-[#6B4A4E]">{t("reserve.guests")}:</span> <span className="text-[#2A0708]">{confirmed.party_size}</span></p>
          </div>
          <button onClick={() => setConfirmed(null)} className="btn-outline-gold mt-6" data-testid="new-reservation-btn">{t("reserve.makeAnother")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-12 min-h-screen" data-testid="reservations-page">
      <div className="container-tulsi">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.4em] uppercase text-[#D11D21] mb-3">{t("reserve.subtitle")}</p>
          <h1 className="font-serif-display text-6xl text-gold-gradient">{t("reserve.title")}</h1>
          <div className="gold-separator" />
          <p className="text-[#6B4A4E] mt-4 max-w-xl mx-auto text-sm">{t("reserve.closedNote")}</p>
        </div>

        <form onSubmit={submit} className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto" data-testid="reservation-form">
          <div className="glass-card p-6">
            <h3 className="font-serif-display text-2xl text-[#2A0708] mb-4 flex items-center gap-2">
              <CalendarIcon size={20} className="text-[#C9A227]" /> {t("reserve.pickDate")}
            </h3>
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => {
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  return d < today || d.getDay() === 2;
                }}
                className="rounded-md"
                data-testid="reservation-calendar"
              />
            </div>
            <h3 className="font-serif-display text-2xl text-[#2A0708] mt-6 mb-3 flex items-center gap-2">
              <Clock size={20} className="text-[#C9A227]" /> {t("reserve.pickTime")}
            </h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="time-slots">
              {TIME_SLOTS.map((tt) => (
                <button type="button" key={tt} onClick={() => setTime(tt)} data-testid={`time-${tt}`}
                  className={`px-3 py-2 text-sm border rounded-md transition-all ${time === tt ? "bg-gradient-to-r from-[#D11D21] to-[#C9A227] text-[#FFFAEF] border-transparent" : "text-[#6B4A4E] border-[#E8D9BE] hover:border-[#C9A227]/50"}`}>
                  {tt}
                </button>
              ))}
            </div>
          </div>

          <div className="glass-card p-6">
            <h3 className="font-serif-display text-2xl text-[#2A0708] mb-4">{t("reserve.yourDetails")}</h3>
            <div className="space-y-4">
              <Field icon={User} placeholder={t("reserve.fullName")} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="res-name" />
              <Field icon={Phone} placeholder={t("reserve.phone")} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="res-phone" />
              <Field icon={Mail} placeholder={t("reserve.email")} value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="res-email" />
              <div className="flex items-center gap-3 px-3 py-2 rounded-md border border-[#E8D9BE] bg-[#FFFAEF]/60">
                <Users size={16} className="text-[#C9A227]" />
                <span className="text-sm text-[#6B4A4E]">{t("reserve.partySize")}</span>
                <input type="number" min="1" max="20" value={form.party_size} onChange={(e) => setForm({ ...form, party_size: e.target.value })} className="ml-auto w-16 bg-transparent text-right text-[#2A0708] outline-none" data-testid="res-party-size" />
              </div>
              <textarea rows={3} placeholder={t("reserve.specialReq")} value={form.special_requests} onChange={(e) => setForm({ ...form, special_requests: e.target.value })}
                className="w-full bg-[#FFFAEF]/60 border border-[#E8D9BE] rounded-md px-3 py-2 text-sm text-[#2A0708] outline-none focus:border-[#C9A227] resize-none"
                data-testid="res-requests" />
            </div>
            <motion.button whileTap={{ scale: 0.97 }} type="submit" disabled={submitting} className="btn-saffron w-full justify-center mt-6 disabled:opacity-50" data-testid="submit-reservation-btn">
              {submitting ? t("reserve.sending") : t("reserve.submit")}
            </motion.button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ icon: Icon, placeholder, value, onChange, testId }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-[#E8D9BE] bg-[#FFFAEF]/60 focus-within:border-[#C9A227]">
      <Icon size={16} className="text-[#C9A227]" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="bg-transparent outline-none text-sm text-[#2A0708] flex-1 placeholder:text-[#6B4A4E]/40" data-testid={testId} />
    </div>
  );
}
